import alert_mixin from "@/utils/mixins/alert_mixin";
import {
    first_non_empty_number,
    parse_response_data,
    reset_runtime_state,
    set_busy_state
} from "./configure_signers_helpers";
import {
    fetch_assigned_signers_by_step,
    fetch_signer_config,
    fetch_user_options_by_entity_type,
    fetch_workflow_assignment,
    fetch_workflow_details,
    load_signer_configuration,
    normalize_workflow_steps
} from "./configure_signers_load_helpers";
import {
    build_signer_slots,
    can_move_slot,
    format_user_label,
    get_available_user_options,
    get_internal_slot_capacity,
    get_role_label,
    get_role_label_lower,
    get_selected_user_ids_for_step,
    get_slot_badge,
    get_slot_display_name,
    get_slot_initials,
    get_slots_for_step,
    get_user_by_id,
    is_user_option_disabled,
    move_slot,
    on_user_selection_change,
    should_show_internal_selector,
    switch_slot_to_internal,
    sync_slot_source_types
} from "./configure_signers_slot_helpers";
import {
    build_configured_summary,
    build_signer_payloads_by_step,
    build_validation_message,
    submit_configuration,
    validate_slots
} from "./configure_signers_submit_helpers";

export default {
    name: "ConfigureSigners",
    mixins: [alert_mixin],
    props: {
        selected_template: {
            type: Object,
            default: null
        },
        entity_profile_id: {
            type: Number,
            default: null
        },
        entity_type_id: {
            type: Number,
            default: null
        }
    },
    emits: ["busy-change", "configured"],
    data: () => ({
        loading: false,
        saving: false,
        load_error: "",
        signing_method: "electronic",
        notify_in_order: true,
        signer_configuration: null,
        workflow_assignment_id: null,
        workflow_id: null,
        workflow_name: "",
        workflow_steps: [],
        signer_slots: []
    }),
    computed: {
        selected_document_name() {
            return this.selected_template?.name || "the selected template";
        },
        has_real_signer_slots() {
            return Array.isArray(this.signer_slots) && this.signer_slots.length > 0;
        },
        display_signer_slots() {
            return this.has_real_signer_slots ? this.signer_slots : [];
        },
        signer_state_message() {
            if (this.loading) {
                return "Loading signer configuration...";
            }

            if (this.load_error) {
                return this.load_error;
            }

            if (!this.has_real_signer_slots) {
                return "No signer roles are available for this template yet.";
            }

            return "";
        },
        template_document_entity_type_id() {
            return first_non_empty_number(
                this.selected_template?.template_document_entity_type_id,
                this.selected_template?.template_document_entity_type,
                this.selected_template?.template_id
            );
        },
        signer_count_label() {
            const count = this.display_signer_slots.length;
            return count === 1 ? "1 signer" : `${count} signers`;
        },
        signing_options() {
            return [
                {
                    id: "electronic",
                    label: "Electronic",
                    description: "Email each signer a secure signing link. Fastest, fully tracked. Best when everyone has email.",
                    recommended: true
                }
            ];
        },
        template_context_ready() {
            return Boolean(this.template_document_entity_type_id && this.entity_profile_id);
        }
    },
    watch: {
        selected_template: {
            immediate: true,
            handler() {
                this.load_signer_configuration();
            }
        },
        entity_profile_id() {
            this.load_signer_configuration();
        }
    },
    methods: {
        parse_response_data,
        set_busy_state(is_busy) {
            const busy_state = set_busy_state(this, is_busy);
            return busy_state;
        },
        reset_runtime_state() {
            const runtime_state = reset_runtime_state(this);
            return runtime_state;
        },
        async load_signer_configuration() {
            return load_signer_configuration(this);
        },
        async fetch_signer_config() {
            return fetch_signer_config(this);
        },
        async fetch_workflow_assignment() {
            return fetch_workflow_assignment(this);
        },
        async fetch_workflow_details(workflow_id) {
            return fetch_workflow_details(this, workflow_id);
        },
        normalize_workflow_steps,
        async fetch_user_options_by_entity_type(workflow_steps) {
            return fetch_user_options_by_entity_type(this, workflow_steps);
        },
        async fetch_assigned_signers_by_step(workflow_assignment_id, workflow_steps) {
            return fetch_assigned_signers_by_step(this, workflow_assignment_id, workflow_steps);
        },
        build_signer_slots,
        get_slots_for_step(step_id) {
            return get_slots_for_step(this, step_id);
        },
        get_internal_slot_capacity(slot) {
            return get_internal_slot_capacity(this, slot);
        },
        sync_slot_source_types() {
            return sync_slot_source_types(this);
        },
        get_role_label,
        get_roleLabelLower(slot) {
            return get_role_label_lower(slot);
        },
        get_role_label_lower(slot) {
            return get_role_label_lower(slot);
        },
        get_slot_display_name(slot) {
            return get_slot_display_name(slot);
        },
        get_slot_initials(slot) {
            return get_slot_initials(slot);
        },
        get_slot_badge,
        get_user_by_id,
        get_selected_user_ids_for_step(slot) {
            return get_selected_user_ids_for_step(this, slot);
        },
        is_user_option_disabled(slot, user) {
            return is_user_option_disabled(this, slot, user);
        },
        get_available_user_options(slot) {
            return get_available_user_options(this, slot);
        },
        should_show_internal_selector(slot) {
            return should_show_internal_selector(this, slot);
        },
        format_user_label,
        on_user_selection_change(slot) {
            return on_user_selection_change(this, slot);
        },
        switch_slot_to_internal(slot) {
            return switch_slot_to_internal(this, slot);
        },
        move_slot(index, direction) {
            return move_slot(this, index, direction);
        },
        can_move_slot(index, direction) {
            return can_move_slot(this, index, direction);
        },
        build_validation_message(slot) {
            return build_validation_message(slot);
        },
        validate_slots() {
            return validate_slots(this);
        },
        build_signer_payloads_by_step() {
            return build_signer_payloads_by_step(this);
        },
        build_configured_summary() {
            return build_configured_summary(this);
        },
        async submit_configuration() {
            return submit_configuration(this);
        }
    }
};
